import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Boxes, ShieldCheck } from 'lucide-react';
import { useSignedOutSystemName } from '@/hooks/useSystemName';

/**
 * The page anyone signing in sees.
 *
 * Two halves: the form, given room and nothing to compete with, and a panel
 * that says what the system is for. The panel is built from the brand tokens
 * rather than an image, so it costs nothing to load, follows whatever colours
 * this device has chosen, and is simply not rendered on a phone — where the
 * only thing that matters is the keyboard and the two fields above it.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const systemName = useSignedOutSystemName();

  return (
    <div className="grid min-h-screen lg:grid-cols-[1fr_1.05fr]">
      <div className="relative flex flex-col justify-center px-6 py-10 sm:px-12">
        <div className="mx-auto w-full max-w-sm">
          <Link to="/" className="mb-10 flex items-center gap-2.5">
            <span className="grid size-10 place-items-center rounded-xl bg-brand-600 font-bold text-white shadow-sm">
              {systemName[0]}
            </span>
            <span className="text-lg font-semibold tracking-tight">{systemName}</span>
          </Link>

          <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-secondary">{subtitle}</p>}

          <div className="mt-8">{children}</div>

          {footer && <div className="mt-8 text-sm text-secondary">{footer}</div>}
        </div>

        <p className="mt-10 text-center text-xs text-muted lg:absolute lg:bottom-6 lg:left-0 lg:right-0 lg:mt-0">
          {systemName} · every unit and every shilling, recorded and attributable
        </p>
      </div>

      <ShowcasePanel systemName={systemName} />
    </div>
  );
}

/**
 * The right-hand half: what the system does, shown rather than claimed.
 *
 * The figures are illustrative and say so — a made-up number presented as
 * live data on a sign-in page is the kind of thing that ends up in a
 * screenshot somewhere it should not be.
 */
function ShowcasePanel({ systemName }: { systemName: string }) {
  return (
    <div className="relative hidden overflow-hidden bg-brand-700 lg:block">
      {/* Light cast across the panel, built from the brand ramp so it follows
          whatever colours this device is themed with. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-90"
        style={{
          background:
            'radial-gradient(70% 60% at 15% 10%, var(--color-brand-500) 0%, transparent 60%),' +
            'radial-gradient(60% 55% at 90% 25%, var(--color-brand-400) 0%, transparent 55%),' +
            'radial-gradient(75% 70% at 60% 100%, var(--color-brand-900) 0%, transparent 65%)',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #fff 1px, transparent 1px),' +
            'linear-gradient(to bottom, #fff 1px, transparent 1px)',
          backgroundSize: '52px 52px',
          maskImage: 'radial-gradient(70% 70% at 50% 40%, #000 30%, transparent 100%)',
        }}
      />

      <div className="relative flex h-full flex-col justify-center gap-10 px-14 py-16 text-white">
        <div>
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-white/70">
            {systemName}
          </p>
          <p className="mt-4 max-w-md text-[2rem] font-semibold leading-tight tracking-tight">
            Run the shop, the stock and the books from one place.
          </p>
          <p className="mt-4 max-w-md text-white/80">
            Multi-branch inventory, sales, procurement and cash — every movement written down
            once, and readable afterwards.
          </p>
        </div>

        <Glimpse />

        <dl className="grid max-w-lg grid-cols-3 gap-6">
          {[
            [Boxes, 'Append-only', 'Stock is a ledger, not a number someone edited.'],
            [Activity, 'Audited', 'Who did what, when, and what it was before.'],
            [ShieldCheck, 'Role-scoped', 'People see the branches and figures they should.'],
          ].map(([Icon, term, detail]) => {
            const Glyph = Icon as typeof Boxes;
            return (
              <div key={term as string}>
                <Glyph className="mb-2 size-5 text-white/80" aria-hidden />
                <dt className="text-sm font-semibold">{term as string}</dt>
                <dd className="mt-1 text-xs leading-relaxed text-white/70">{detail as string}</dd>
              </div>
            );
          })}
        </dl>
      </div>
    </div>
  );
}

/** A glimpse of the dashboard, drawn rather than screenshotted. */
function Glimpse() {
  const bars = [38, 52, 44, 66, 58, 79, 71];

  return (
    <div className="max-w-md rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-white/60">Revenue this week</p>
          <p className="mt-1 text-2xl font-semibold tabular">USh 12,480,000</p>
        </div>
        <span className="rounded-full bg-white/15 px-2 py-1 text-xs font-medium">+18%</span>
      </div>

      <div className="mt-5 flex h-20 items-end gap-2" aria-hidden>
        {bars.map((height, index) => (
          <span
            key={index}
            className="flex-1 rounded-t bg-white/70"
            style={{ height: `${height}%`, opacity: 0.45 + index * 0.08 }}
          />
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-white/15 pt-3 text-xs text-white/70">
        <span>Kireka · Nakawa · Mukono</span>
        <span>Illustrative figures</span>
      </div>
    </div>
  );
}
